import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, PollData, PromptData, PublicUser, KudosEntry, ChecklistItem } from './api';
import { Select } from './Select';
import { Icon } from './icons';
import { avatarStyle } from './AccountMenu';

const initial = (s?: string) => (s?.trim()?.[0] || '?').toUpperCase();

// ── Poll card (rendered inline on a poll message) ─────────────────────────
export function PollCard({
  pollId,
  live,
  members,
}: {
  pollId: string;
  live?: { counts: Record<string, number>; total_voters: number; closed: boolean };
  members?: PublicUser[];
}) {
  const [poll, setPoll] = useState<PollData | null>(null);
  const [busy, setBusy] = useState(false);
  const [showVoters, setShowVoters] = useState(false);
  const nameOf = (id: string) => {
    const m = members?.find((x) => x.id === id);
    return m?.name || m?.email || 'Someone';
  };

  useEffect(() => {
    let alive = true;
    api.getPoll(pollId).then((p) => { if (alive) setPoll(p); }).catch(() => {});
    return () => { alive = false; };
  }, [pollId]);

  // Merge in live counts pushed to the channel (others voting / poll closed).
  const view = useMemo(() => {
    if (!poll) return null;
    if (!live) return poll;
    return { ...poll, counts: live.counts, total_voters: live.total_voters, closed: poll.closed || live.closed };
  }, [poll, live]);

  if (!view) return <div className="fx-poll fx-poll--loading">Loading poll…</div>;

  const total = Object.values(view.counts).reduce((a, b) => a + b, 0) || 0;
  const closed = view.closed || (!!view.closes_at && new Date(view.closes_at).getTime() < Date.now());

  const vote = async (optionId: string) => {
    if (closed || busy) return;
    const mine = new Set(view.my_votes);
    let next: string[];
    if (view.allow_multi) {
      next = mine.has(optionId) ? [...mine].filter((x) => x !== optionId) : [...mine, optionId];
    } else {
      next = mine.has(optionId) ? [] : [optionId];
    }
    setBusy(true);
    try { setPoll(await api.votePoll(pollId, next)); } catch { /* ignore */ } finally { setBusy(false); }
  };
  const close = async () => {
    setBusy(true);
    try { setPoll(await api.closePoll(pollId)); } catch { /* not the owner/admin */ } finally { setBusy(false); }
  };

  return (
    <div className="fx-poll">
      <div className="fx-poll-q">
        <Icon name="activity" size={15} /> <span>{view.question}</span>
      </div>
      <div className="fx-poll-opts">
        {view.options.map((o) => {
          const n = view.counts[o.id] || 0;
          const pct = total ? Math.round((n / total) * 100) : 0;
          const picked = view.my_votes.includes(o.id);
          return (
            <button
              type="button"
              key={o.id}
              className={`fx-poll-opt${picked ? ' is-picked' : ''}${closed ? ' is-closed' : ''}`}
              onClick={() => vote(o.id)}
              disabled={closed || busy}
            >
              <span className="fx-poll-fill" style={{ width: `${pct}%` }} />
              <span className="fx-poll-check">{picked ? <Icon name="markread" size={13} /> : null}</span>
              <span className="fx-poll-text">{o.text}</span>
              <span className="fx-poll-pct">{pct}% · {n}</span>
            </button>
          );
        })}
      </div>
      <div className="fx-poll-foot">
        <span>{view.total_voters} vote{view.total_voters === 1 ? '' : 's'}{view.allow_multi ? ' · multi-select' : ''}{view.anonymous ? ' · anonymous' : ''}</span>
        <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {!view.anonymous && view.total_voters > 0 && (
            <button type="button" className="fx-poll-closebtn" onClick={() => setShowVoters((v) => !v)}>
              {showVoters ? 'Hide voters' : 'See who voted'}
            </button>
          )}
          {closed ? <span className="fx-poll-closed">Closed</span> : (
            <button type="button" className="fx-poll-closebtn" onClick={close} disabled={busy}>Close poll</button>
          )}
        </span>
      </div>
      {showVoters && !view.anonymous && (
        <div style={{ marginTop: 8, borderTop: '1px solid var(--fx-hair, rgba(255,255,255,0.16))', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {view.options.map((o) => {
            const ids = view.voters_by_option?.[o.id] || [];
            return (
              <div key={o.id} style={{ fontSize: 12, lineHeight: 1.45 }}>
                <span style={{ color: 'var(--fx-muted,#9aa3b7)' }}>{o.text}: </span>
                <span>{ids.length ? ids.map(nameOf).join(', ') : '—'}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Prompt card (an agent's structured question — single/multi select) ─────
// Rendered inline on a message carrying {type:"prompt", prompt_id}. The user
// picks option(s) and submits; the answer locks the card and resumes the agent.
export function PromptCard({
  promptId,
  live,
}: {
  promptId: string;
  live?: { status: string; selected: string[] };
}) {
  const [prompt, setPrompt] = useState<PromptData | null>(null);
  const [pick, setPick] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.getPrompt(promptId).then((p) => { if (alive) setPrompt(p); }).catch(() => {});
    return () => { alive = false; };
  }, [promptId]);

  const view = useMemo(() => {
    if (!prompt) return null;
    if (!live) return prompt;
    return { ...prompt, status: (live.status as PromptData['status']) || prompt.status, selected: live.selected || prompt.selected };
  }, [prompt, live]);

  if (!view) return <div className="fx-poll fx-poll--loading">Loading question…</div>;

  const answered = view.status === 'answered';
  const selectedSet = new Set(view.selected);

  const toggle = (id: string) => {
    if (answered || busy) return;
    setPick((cur) => {
      const next = new Set(view.allow_multi ? cur : []);
      if (cur.has(id) && view.allow_multi) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (answered || busy || pick.size === 0) return;
    setBusy(true);
    try { setPrompt(await api.answerPrompt(promptId, [...pick])); }
    catch { /* already answered / no access */ }
    finally { setBusy(false); }
  };

  return (
    <div className="fx-prompt">
      <div className="fx-prompt-q">
        <Icon name="bolt" size={15} /> <span>{view.question}</span>
      </div>
      <div className="fx-prompt-opts">
        {view.options.map((o) => {
          const chosen = answered ? selectedSet.has(o.id) : pick.has(o.id);
          return (
            <button
              type="button"
              key={o.id}
              className={`fx-prompt-opt${chosen ? ' is-picked' : ''}${answered ? ' is-locked' : ''}`}
              onClick={() => toggle(o.id)}
              disabled={answered || busy}
            >
              <span className={`fx-prompt-mark${view.allow_multi ? ' is-box' : ''}`}>
                {chosen ? <Icon name="markread" size={12} /> : null}
              </span>
              <span className="fx-prompt-text">{o.label}</span>
            </button>
          );
        })}
      </div>
      <div className="fx-prompt-foot">
        {answered ? (
          <span className="fx-prompt-done"><Icon name="markread" size={12} /> Answered</span>
        ) : (
          <>
            {view.allow_multi && <span className="fx-prompt-hint">Select one or more</span>}
            <button type="button" className="fx-prompt-submit" onClick={submit} disabled={busy || pick.size === 0}>
              {busy ? 'Sending…' : 'Submit'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Create-poll modal ─────────────────────────────────────────────────────
export function CreatePollModal({
  onCreate,
  onClose,
}: {
  onCreate: (question: string, options: string[], opts: { allow_multi: boolean; anonymous: boolean }) => Promise<void>;
  onClose: () => void;
}) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [allowMulti, setAllowMulti] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const setOpt = (i: number, v: string) => setOptions((o) => o.map((x, j) => (j === i ? v : x)));
  const addOpt = () => setOptions((o) => (o.length < 10 ? [...o, ''] : o));
  const rmOpt = (i: number) => setOptions((o) => (o.length > 2 ? o.filter((_, j) => j !== i) : o));

  const submit = async () => {
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim() || opts.length < 2) { setErr('Add a question and at least 2 options'); return; }
    setBusy(true); setErr('');
    try { await onCreate(question.trim(), opts, { allow_multi: allowMulti, anonymous }); onClose(); }
    catch (e: any) { setErr(e?.message || 'Could not create poll'); setBusy(false); }
  };

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Create a poll</div>
        <input className="fx-text-input" placeholder="Ask a question…" value={question} autoFocus onChange={(e) => setQuestion(e.target.value)} />
        <div className="fx-poll-optedit">
          {options.map((o, i) => (
            <div className="fx-poll-optrow" key={i}>
              <input className="fx-text-input" placeholder={`Option ${i + 1}`} value={o} onChange={(e) => setOpt(i, e.target.value)} />
              {options.length > 2 && (
                <button type="button" className="fx-poll-optrm" title="Remove" onClick={() => rmOpt(i)}><Icon name="close" size={14} /></button>
              )}
            </div>
          ))}
          {options.length < 10 && (
            <button type="button" className="fx-poll-addopt" onClick={addOpt}><Icon name="plus" size={14} /> Add option</button>
          )}
        </div>
        <label className="fx-check-row"><input type="checkbox" checked={allowMulti} onChange={(e) => setAllowMulti(e.target.checked)} /> <span>Allow multiple choices</span></label>
        <label className="fx-check-row"><input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} /> <span>Anonymous votes</span></label>
        {err && <div className="fx-login-err">{err}</div>}
        <div className="fx-modal-actions">
          <button type="button" className="fx-sendbtn" onClick={submit} disabled={busy}>{busy ? 'Creating…' : 'Create poll'}</button>
          <button type="button" className="fx-login-back" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Kudos: leaderboard + recent + give ────────────────────────────────────
export function KudosModal({
  wid,
  members,
  meId,
  onClose,
}: {
  wid: string;
  members: PublicUser[];
  meId?: string;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'board' | 'recent' | 'give'>('board');
  const [board, setBoard] = useState<{ user: PublicUser; count: number }[] | null>(null);
  const [feed, setFeed] = useState<KudosEntry[] | null>(null);
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.kudosLeaderboard(wid).then(setBoard).catch(() => setBoard([])); }, [wid]);
  useEffect(() => { if (tab === 'recent' && !feed) api.kudosFeed(wid).then(setFeed).catch(() => setFeed([])); }, [tab, feed, wid]);

  const give = async () => {
    if (!to) { setNote('Pick someone'); return; }
    setBusy(true); setNote('');
    try {
      await api.giveKudos(wid, to, reason.trim());
      setNote('Kudos sent 🎉'); setReason(''); setTo('');
      api.kudosLeaderboard(wid).then(setBoard).catch(() => {});
      setFeed(null);
    } catch (e: any) { setNote(e?.message || 'Could not send'); }
    finally { setBusy(false); }
  };

  const av = (u: PublicUser) => (
    <span className="fx-kudos-av" style={avatarStyle(u)}>{u.avatar_url ? '' : initial(u.name || u.email)}</span>
  );

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-bots" onClick={(e) => e.stopPropagation()}>
        <div className="fx-bots-head">
          <div className="fx-login-title">🎉 Kudos</div>
          <div className="fx-bots-tabs">
            <button type="button" className={`fx-bots-tab${tab === 'board' ? ' is-active' : ''}`} onClick={() => setTab('board')}>Leaderboard</button>
            <button type="button" className={`fx-bots-tab${tab === 'recent' ? ' is-active' : ''}`} onClick={() => setTab('recent')}>Recent</button>
            <button type="button" className={`fx-bots-tab${tab === 'give' ? ' is-active' : ''}`} onClick={() => setTab('give')}>Give kudos</button>
          </div>
        </div>
        <div className="fx-bots-body">
          {tab === 'board' && (
            <div className="fx-kudos-board">
              {board?.length === 0 && <div className="fx-empty">No kudos yet — be the first to recognize someone.</div>}
              {(board || []).map((r, i) => (
                <div className="fx-kudos-row" key={r.user.id}>
                  <span className="fx-kudos-rank">{i + 1}</span>
                  {av(r.user)}
                  <span className="fx-kudos-name">{r.user.name || r.user.email}</span>
                  <span className="fx-kudos-count">{r.count} ⭐</span>
                </div>
              ))}
            </div>
          )}
          {tab === 'recent' && (
            <div className="fx-kudos-feed">
              {feed?.length === 0 && <div className="fx-empty">No kudos yet.</div>}
              {(feed || []).map((k) => (
                <div className="fx-kudos-item" key={k.id}>
                  {av(k.to)}
                  <div className="fx-kudos-txt">
                    <span><b>{k.from.name || k.from.email}</b> → <b>{k.to.name || k.to.email}</b></span>
                    {k.reason && <span className="fx-kudos-reason">{k.reason}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {tab === 'give' && (
            <div className="fx-kudos-give">
              <div className="fx-invite-ch-h">To</div>
              <Select value={to} onChange={setTo} placeholder="Choose a teammate…" style={{ width: '100%' }}
                options={members.filter((m) => m.id !== meId).map((m) => ({ value: m.id, label: m.name || m.email || '' }))} />
              <textarea className="fx-text-input fx-invite-emails" placeholder="What are they being recognized for? (optional)" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
              {note && <div className="fx-login-note" style={{ marginTop: 6 }}>{note}</div>}
              <button type="button" className="fx-sendbtn fx-login-btn" onClick={give} disabled={busy}>{busy ? 'Sending…' : 'Give kudos'}</button>
            </div>
          )}
        </div>
        <div className="fx-bots-foot"><button type="button" className="fx-login-back" onClick={onClose}>Done</button></div>
      </div>
    </div>
  );
}

// ── Channel checklist ─────────────────────────────────────────────────────
export function ChecklistModal({
  cid,
  channelName,
  onClose,
}: {
  cid: string;
  channelName: string;
  onClose: () => void;
}) {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const load = () => api.getChecklist(cid).then(setItems).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cid]);

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setText('');
    try { const it = await api.addChecklistItem(cid, t); setItems((c) => [...c, it]); } catch { /* ignore */ }
    inputRef.current?.focus();
  };
  const toggle = async (it: ChecklistItem) => {
    setItems((c) => c.map((x) => (x.id === it.id ? { ...x, done: !x.done } : x)));
    try { await api.updateChecklistItem(it.id, { done: !it.done }); } catch { load(); }
  };
  const remove = async (it: ChecklistItem) => {
    setItems((c) => c.filter((x) => x.id !== it.id));
    try { await api.deleteChecklistItem(it.id); } catch { load(); }
  };

  const done = items.filter((i) => i.done).length;
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">✓ Checklist — {channelName}</div>
        <div className="fx-login-note" style={{ textAlign: 'left', marginTop: 0 }}>
          {items.length ? `${done}/${items.length} done` : 'A shared to-do list for this channel.'}
        </div>
        <div className="fx-checklist">
          {items.map((it) => (
            <div className={`fx-check-item${it.done ? ' is-done' : ''}`} key={it.id}>
              <button type="button" className="fx-check-box" onClick={() => toggle(it)} aria-label={it.done ? 'Mark undone' : 'Mark done'}>
                {it.done ? <Icon name="markread" size={13} /> : null}
              </button>
              <span className="fx-check-text">{it.text}</span>
              <button type="button" className="fx-check-rm" title="Remove" onClick={() => remove(it)}><Icon name="close" size={13} /></button>
            </div>
          ))}
        </div>
        <div className="fx-check-add">
          <input ref={inputRef} className="fx-text-input" placeholder="Add an item…" value={text}
            onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
          <button type="button" className="fx-sendbtn" onClick={add}>Add</button>
        </div>
        <div className="fx-modal-actions"><button type="button" className="fx-login-back" onClick={onClose}>Done</button></div>
      </div>
    </div>
  );
}
